import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import CableIcon from '@mui/icons-material/Cable';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FunctionsIcon from '@mui/icons-material/Functions';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';
import {
  MaterialType,
  RHO_ALUMINIUM,
  RHO_KUPFER,
  formatDecimal,
  getCapacityTable,
  getTemperatureCorrectionFactor
} from '../../utils/cableTables';

type CalculationTarget = 'POWER' | 'CURRENT' | 'VOLTAGE' | 'REACTIVE_POWER' | 'EFFICIENCY';
type SnackbarState = { open: boolean; message: string; severity: 'success' | 'info' | 'warning' | 'error' };

type LastCalculationState = {
  current: number | null;
  voltage: number | null;
  power: number | null;
  cosPhi: number | null;
  reactivePower: number | null;
  apparentPower: number | null;
};

const voltageSystems = ['DC', 'AC 1-phasig', 'AC 3-phasig'] as const;
const calculationTargets = [
  { value: 'POWER' as CalculationTarget, label: 'Wirkleistung berechnen' },
  { value: 'CURRENT' as CalculationTarget, label: 'Strom berechnen' },
  { value: 'VOLTAGE' as CalculationTarget, label: 'Spannung berechnen' },
  { value: 'REACTIVE_POWER' as CalculationTarget, label: 'Blindleistung berechnen' },
  { value: 'EFFICIENCY' as CalculationTarget, label: 'Wirkungsgrad berechnen' }
];

const infoTopics = [
  {
    title: 'Grundformeln Gleichstrom (DC)',
    html: `
      <b>Leistung:</b> P = U · I = I² · R = U² / R<br>
      <b>Spannung:</b> U = P / I = I · R = √(P · R)<br>
      <b>Strom:</b> I = P / U = U / R = √(P / R)<br>
      <b>Widerstand:</b> R = U / I = P / I² = U² / P<br><br>
      <i>Legende:</i> P = Leistung [W], U = Spannung [V], I = Strom [A], R = Widerstand [Î©]
    `
  },
  {
    title: 'Wechselstrom 1-phasig (AC)',
    html: `
      <b>Wirkleistung:</b> P = U · I · cos φ<br>
      <b>Scheinleistung:</b> S = U · I [VA]<br>
      <b>Blindleistung:</b> Q = U · I · sin φ = √(S² - P²) [var]<br>
      <b>Leistungsfaktor:</b> cos φ = P / S<br>
      <b>Strom:</b> I = P / (U · cos φ) oder I = S / U<br><br>
      <i>Hinweis:</i> Bei rein ohmscher Last ist cos Ï† = 1 (keine Blindleistung)
    `
  },
  {
    title: 'Drehstrom 3-phasig (AC)',
    html: `
      <b>Wirkleistung:</b> P = √3 · U · I · cos φ<br>
      <b>Scheinleistung:</b> S = √3 · U · I [VA]<br>
      <b>Blindleistung:</b> Q = √3 · U · I · sin φ [var]<br>
      <b>Strom:</b> I = P / (√3 · U · cos φ)<br><br>
      <i>U = Verkettete Spannung (Außenleiter-Außenleiter)</i><br>
      <i>Beispiel: 400 V Drehstrom → U = 400 V</i>
    `
  },
  {
    title: 'Wirkungsgrad & Verluste',
    html: `
      <b>Wirkungsgrad:</b> η = P<sub>ab</sub> / P<sub>zu</sub> = P<sub>Nutz</sub> / P<sub>Gesamt</sub><br>
      <b>Verlustleistung:</b> P<sub>V</sub> = P<sub>zu</sub> - P<sub>ab</sub><br>
      <b>Wirkungsgrad in %:</b> η [%] = (P<sub>ab</sub> / P<sub>zu</sub>) · 100<br><br>
      <b>Beispiel Motor:</b><br>
      P<sub>elektrisch</sub> = 10 kW, P<sub>mechanisch</sub> = 9,2 kW<br>
      → η = 9,2 / 10 = 0,92 = 92%<br>
      → Verlust = 0,8 kW (als Wärme)
    `
  },
  {
    title: 'Kabelquerschnitt & Spannungsfall',
    html: `
      <b>Kriterien für Leitungsquerschnitt:</b><br>
      1. <b>Strombelastbarkeit (I<sub>z</sub>):</b> Querschnitt muss thermisch ausreichend sein<br>
      2. <b>Spannungsfall (ΔU):</b> Max. 3% für Beleuchtung, 4% für Kraft<br><br>
      <b>Spannungsfall berechnen:</b><br>
      ΔU [V] = (2 · L · I · ρ) / A  (DC / AC 1-phasig)<br>
      ΔU [V] = (√3 · L · I · ρ · cos φ) / A  (AC 3-phasig)<br><br>
      <i>L = Leitungslänge [m], ρ = 0,0178 (Cu) / 0,0282 (Al) [Ω·mm²/m], A = Querschnitt [mm²]</i>
    `
  }
];

const installationTypes = [
  'A1 - Rohr in wärmegedämmter Wand (Aderltg.)',
  'A2 - Rohr in wärmegedämmter Wand (Kabel)',
  'B1 - Rohr auf/in Wand (Aderltg.)',
  'B2 - Rohr auf/in Wand (Kabel)',
  'C - Auf Wand / Kabelrinne (gelocht)',
  'E - Frei in Luft (Abstand Wand)',
  'F - Einadrig, berührend, frei in Luft',
  'G - Einadrig, Abstand, frei in Luft'
];

const conductorMaterials: MaterialType[] = ['Kupfer', 'Aluminium'];
const defaultPreviewInstallation = 'C - Auf Wand / Kabelrinne (gelocht)';

// SVG: Leistungsdreieck (P, Q, S mit cos Ï†)
const PowerTriangleSVG: React.FC<{ cosPhi: number; darkMode: boolean }> = ({ cosPhi, darkMode }) => {
  const textColor = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;
  const phi = Math.acos(cosPhi) * (180 / Math.PI);
  const pLength = 100 * cosPhi;
  const qLength = 100 * Math.sin(Math.acos(cosPhi));
  
  return (
    <svg viewBox="0 0 160 120" style={{ maxWidth: 300, width: '100%', height: 'auto' }}>
      <defs>
        <marker id="arrowP" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L9,3 z" fill={tokens.colors.primary} />
        </marker>
        <marker id="arrowQ" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L9,3 z" fill="#FF9800" />
        </marker>
        <marker id="arrowS" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L9,3 z" fill="#4CAF50" />
        </marker>
      </defs>
      
      {/* Wirkleistung P (horizontal) */}
      <line x1="20" y1="90" x2={20 + pLength} y2="90" stroke={tokens.colors.primary} strokeWidth="2" markerEnd="url(#arrowP)" />
      <text x={20 + pLength/2} y="85" fill={textColor} fontSize="12" textAnchor="middle" fontWeight="600">P (W)</text>
      
      {/* Blindleistung Q (vertikal) */}
      <line x1={20 + pLength} y1="90" x2={20 + pLength} y2={90 - qLength} stroke="#FF9800" strokeWidth="2" markerEnd="url(#arrowQ)" />
      <text x={20 + pLength + 15} y={90 - qLength/2} fill={textColor} fontSize="12" fontWeight="600">Q (var)</text>
      
      {/* Scheinleistung S (Hypotenuse) */}
      <line x1="20" y1="90" x2={20 + pLength} y2={90 - qLength} stroke="#4CAF50" strokeWidth="2" strokeDasharray="4 2" markerEnd="url(#arrowS)" />
      <text x={20 + pLength/2 - 10} y={90 - qLength/2 - 5} fill={textColor} fontSize="12" fontWeight="600">S (VA)</text>
      
      {/* Winkel Ï† */}
      <path d={`M 30 90 A 10 10 0 0 0 ${30 + 10*cosPhi} ${90 - 10*Math.sin(Math.acos(cosPhi))}`} fill="none" stroke={textColor} strokeWidth="1" />
      <text x="35" y="95" fill={textColor} fontSize="11">φ = {phi.toFixed(1)}°</text>
      
      {/* cos Ï† Beschriftung */}
      <text x="20" y="110" fill={textColor} fontSize="11">cos Ï† = {cosPhi.toFixed(3)}</text>
    </svg>
  );
};

// SVG: Kabelquerschnitt mit Stromfluss
const CableCrossSectionSVG: React.FC<{ crossSection: number; current: number; darkMode: boolean }> = ({ crossSection, current, darkMode }) => {
  const textColor = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;
  const radius = Math.sqrt(crossSection / Math.PI) * 3; // Skalierung für Visualisierung
  
  return (
    <svg viewBox="0 0 140 100" style={{ maxWidth: 250, width: '100%', height: 'auto' }}>
      {/* Kabelquerschnitt */}
      <circle cx="70" cy="50" r={radius} fill="#FFB74D" stroke="#E65100" strokeWidth="2" />
      <circle cx="70" cy="50" r={radius * 0.7} fill="#FFA726" opacity="0.5" />
      
      {/* Stromfluss-Pfeile */}
      <defs>
        <marker id="arrowCurrent" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3 z" fill={tokens.colors.primary} />
        </marker>
      </defs>
      <line x1="10" y1="50" x2="50" y2="50" stroke={tokens.colors.primary} strokeWidth="2" markerEnd="url(#arrowCurrent)" />
      <line x1="90" y1="50" x2="130" y2="50" stroke={tokens.colors.primary} strokeWidth="2" markerEnd="url(#arrowCurrent)" />
      
      {/* Beschriftung */}
      <text x="70" y="50" fill="#FFF" fontSize="14" fontWeight="700" textAnchor="middle" dominantBaseline="middle">
        {crossSection} mm²
      </text>
      <text x="70" y={50 + radius + 15} fill={textColor} fontSize="12" textAnchor="middle">
        I = {formatDecimal(current)} A
      </text>
    </svg>
  );
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewLeistungsberechnung } = useAuth();
  const [calculationTarget, setCalculationTarget] = useState<CalculationTarget>('POWER');
  const [voltageSystem, setVoltageSystem] = useState<typeof voltageSystems[number]>('DC');
  const [powerUnit, setPowerUnit] = useState<'W' | 'VA'>('W');
  const [infoTopicIndex, setInfoTopicIndex] = useState(0);

  const [inputs, setInputs] = useState({
    voltage: '',
    current: '',
    power: '',
    powerInput: '', // P_zu für Wirkungsgrad
    powerOutput: '', // P_ab für Wirkungsgrad
    resistance: '',
    cosPhi: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [cableInputs, setCableInputs] = useState({
    installationType: defaultPreviewInstallation,
    conductorMaterial: 'Kupfer' as MaterialType,
    ambientTemp: '30',
    cableLength: '',
    maxVoltageDrop: '3'
  });
  const [cableErrors, setCableErrors] = useState<Record<string, string>>({});

  const [resultHtml, setResultHtml] = useState('');
  const [cableResultHtml, setCableResultHtml] = useState('');
  const [lastState, setLastState] = useState<LastCalculationState>({
    current: null,
    voltage: null,
    power: null,
    cosPhi: null,
    reactivePower: null,
    apparentPower: null
  });

  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const isAcSystem = voltageSystem.startsWith('AC');

  useEffect(() => {
    if (voltageSystem === 'DC') {
      setPowerUnit('W');
      setInputs(prev => ({ ...prev, cosPhi: '' }));
      if (calculationTarget === 'REACTIVE_POWER') {
        setCalculationTarget('POWER');
      }
    }
  }, [voltageSystem, calculationTarget]);

  useEffect(() => {
    setResultHtml('');
    setCableResultHtml('');
    setLastState({ current: null, voltage: null, power: null, cosPhi: null, reactivePower: null, apparentPower: null });
  }, [calculationTarget, voltageSystem]);

  const numberFromInput = (value: string): number | null => {
    if (!value?.trim()) return null;
    const normalized = value.replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleInputChange = (field: keyof typeof inputs) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputs(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleCableInputChange = (field: keyof typeof cableInputs) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCableInputs(prev => ({ ...prev, [field]: value }));
    setCableErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleReset = useCallback(() => {
    setInputs({
      voltage: '',
      current: '',
      power: '',
      powerInput: '',
      powerOutput: '',
      resistance: '',
      cosPhi: ''
    });
    setCableInputs({
      installationType: defaultPreviewInstallation,
      conductorMaterial: 'Kupfer',
      ambientTemp: '30',
      cableLength: '',
      maxVoltageDrop: '3'
    });
    setErrors({});
    setCableErrors({});
    setResultHtml('');
    setCableResultHtml('');
    setLastState({ current: null, voltage: null, power: null, cosPhi: null, reactivePower: null, apparentPower: null });
    setSnackbar({ open: true, message: 'Alle Felder zurückgesetzt', severity: 'info' });
  }, []);

  const handleCopyResult = useCallback(() => {
    if (!resultHtml) {
      setSnackbar({ open: true, message: 'Kein Ergebnis zum Kopieren vorhanden', severity: 'warning' });
      return;
    }
    const textVersion = resultHtml.replace(/<br>/g, '\n').replace(/<\/?[^>]+(>|$)/g, '');
    navigator.clipboard.writeText(textVersion).then(() => {
      setSnackbar({ open: true, message: 'Ergebnis in Zwischenablage kopiert', severity: 'success' });
    });
  }, [resultHtml]);

  const findPreviewCrossSection = (current: number, material: MaterialType) => {
    const table = getCapacityTable(material, voltageSystem === 'AC 3-phasig' ? 3 : 2, defaultPreviewInstallation);
    if (!table) return null;
    const factor = getTemperatureCorrectionFactor(30);
    for (const [section, baseLoad] of table) {
      if (baseLoad * factor >= current) {
        return section;
      }
    }
    return null;
  };

  const performCalculation = () => {
    setErrors({});
    let message = '';
    const u = numberFromInput(inputs.voltage);
    const i = numberFromInput(inputs.current);
    const pInput = numberFromInput(inputs.power);
    const r = numberFromInput(inputs.resistance);
    const cosPhi = numberFromInput(inputs.cosPhi);
    const pZu = numberFromInput(inputs.powerInput);
    const pAb = numberFromInput(inputs.powerOutput);

    // Validierung cos φ für AC-Systeme
    if (isAcSystem && ['POWER', 'REACTIVE_POWER'].includes(calculationTarget)) {
      if (cosPhi === null || cosPhi <= 0 || cosPhi > 1) {
        setErrors(prev => ({ ...prev, cosPhi: 'cos φ (0 < φ ≤ 1) erforderlich.' }));
        setResultHtml('Eingabefehler: cos φ prüfen.');
        return;
      }
    }

    let calculatedPower: number | null = null;
    let calculatedCurrent: number | null = null;
    let calculatedVoltage: number | null = null;
    let resultingCosPhi: number | null = null;
    let reactivePower: number | null = null;
    let apparentPower: number | null = null;
    let efficiency: number | null = null;

    try {
      switch (calculationTarget) {
        case 'EFFICIENCY': {
          if (pZu && pAb && pZu > 0) {
            efficiency = pAb / pZu;
            const loss = pZu - pAb;
            setResultHtml(
              `<b>Wirkungsgrad (Î·):</b> ${formatDecimal(efficiency * 100)} %<br>` +
              `<b>Zugeführte Leistung:</b> ${formatDecimal(pZu)} W<br>` +
              `<b>Abgegebene Leistung:</b> ${formatDecimal(pAb)} W<br>` +
              `<b>Verlustleistung:</b> ${formatDecimal(loss)} W<br><br>` +
              `<i>Verluste werden meist als Wärme abgegeben.</i>`
            );
            setLastState({ current: null, voltage: null, power: pAb, cosPhi: null, reactivePower: null, apparentPower: null });
          } else {
            message = 'Zugeführte und abgegebene Leistung erforderlich.';
          }
          break;
        }
        
        case 'REACTIVE_POWER': {
          if (!isAcSystem) {
            message = 'Blindleistung nur für AC-Systeme relevant.';
            break;
          }
          if (!u || !i || !cosPhi) {
            message = 'Spannung, Strom und cos Ï† erforderlich.';
            break;
          }
          
          const sinPhi = Math.sqrt(1 - cosPhi * cosPhi);
          if (voltageSystem === 'AC 1-phasig') {
            apparentPower = u * i;
            calculatedPower = apparentPower * cosPhi;
            reactivePower = apparentPower * sinPhi;
          } else { // 3-phasig
            apparentPower = Math.sqrt(3) * u * i;
            calculatedPower = apparentPower * cosPhi;
            reactivePower = apparentPower * sinPhi;
          }
          
          calculatedCurrent = i;
          calculatedVoltage = u;
          resultingCosPhi = cosPhi;
          
          setResultHtml(
            `<b>Blindleistung (Q):</b> ${formatDecimal(reactivePower)} var<br>` +
            `<b>Wirkleistung (P):</b> ${formatDecimal(calculatedPower)} W<br>` +
            `<b>Scheinleistung (S):</b> ${formatDecimal(apparentPower)} VA<br>` +
            `<b>Leistungsfaktor:</b> cos Ï† = ${formatDecimal(cosPhi)}<br><br>` +
            `<i>Formel: Q = S · sin φ = √(S² - P²)</i>`
          );
          setLastState({ 
            current: calculatedCurrent, 
            voltage: calculatedVoltage, 
            power: calculatedPower, 
            cosPhi: resultingCosPhi,
            reactivePower,
            apparentPower
          });
          break;
        }

        case 'POWER': {
          if (u && i) {
            const effectiveCos = isAcSystem ? cosPhi ?? 1 : 1;
            if (voltageSystem === 'DC') {
              calculatedPower = u * i;
              apparentPower = calculatedPower;
              message = `<b>📐 Gleichstrom Leistungsberechnung:</b><br/>` +
                `Formel: P = U × I<br/>` +
                `P = ${u} V × ${i} A = <b>${calculatedPower.toFixed(2)} W</b>`;
            } else if (voltageSystem === 'AC 1-phasig') {
              apparentPower = u * i;
              calculatedPower = apparentPower * effectiveCos;
              const sinPhi = Math.sqrt(1 - effectiveCos * effectiveCos);
              reactivePower = apparentPower * sinPhi;
              message = `<b>📐 Wechselstrom 1-phasig Leistungsberechnung:</b><br/>` +
                `Scheinleistung: S = U × I = ${u} V × ${i} A = <b>${apparentPower.toFixed(2)} VA</b><br/>` +
                `Wirkleistung: P = S × cos φ = ${apparentPower.toFixed(2)} VA × ${effectiveCos.toFixed(3)} = <b>${calculatedPower.toFixed(2)} W</b><br/>` +
                `Blindleistung: Q = S × sin φ = ${apparentPower.toFixed(2)} VA × ${sinPhi.toFixed(3)} = <b>${reactivePower.toFixed(2)} var</b>`;
            } else {
              apparentPower = Math.sqrt(3) * u * i;
              calculatedPower = apparentPower * effectiveCos;
              const sinPhi = Math.sqrt(1 - effectiveCos * effectiveCos);
              reactivePower = apparentPower * sinPhi;
              message = `<b>📐 Drehstrom Leistungsberechnung:</b><br/>` +
                `Scheinleistung: S = √3 × U × I = √3 × ${u} V × ${i} A = <b>${apparentPower.toFixed(2)} VA</b><br/>` +
                `Wirkleistung: P = S × cos φ = ${apparentPower.toFixed(2)} VA × ${effectiveCos.toFixed(3)} = <b>${calculatedPower.toFixed(2)} W</b><br/>` +
                `Blindleistung: Q = S × sin φ = ${apparentPower.toFixed(2)} VA × ${sinPhi.toFixed(3)} = <b>${reactivePower.toFixed(2)} var</b>`;
            }
            calculatedCurrent = i;
            calculatedVoltage = u;
            resultingCosPhi = isAcSystem ? effectiveCos : null;
          } else if (u && r && r > 0) {
            calculatedPower = u * u / r;
            calculatedCurrent = u / r;
            calculatedVoltage = u;
            apparentPower = calculatedPower;
            resultingCosPhi = isAcSystem ? cosPhi ?? null : null;
            message = `<b>📐 Leistungsberechnung mit Spannung und Widerstand:</b><br/>` +
              `Formel: P = U² / R<br/>` +
              `P = ${u}² V / ${r} Ω = ${(u*u).toFixed(2)} / ${r} = <b>${calculatedPower.toFixed(2)} W</b><br/>` +
              `Strom: I = U / R = ${u} V / ${r} Ω = <b>${calculatedCurrent.toFixed(2)} A</b>`;
          } else if (i && r && r > 0) {
            calculatedPower = i * i * r;
            calculatedCurrent = i;
            calculatedVoltage = i * r;
            apparentPower = calculatedPower;
            resultingCosPhi = isAcSystem ? cosPhi ?? null : null;
            message = `<b>📐 Leistungsberechnung mit Strom und Widerstand:</b><br/>` +
              `Formel: P = I² × R<br/>` +
              `P = ${i}² A × ${r} Ω = ${(i*i).toFixed(2)} × ${r} = <b>${calculatedPower.toFixed(2)} W</b><br/>` +
              `Spannung: U = I × R = ${i} A × ${r} Ω = <b>${calculatedVoltage.toFixed(2)} V</b>`;
          } else {
            message = 'Nicht genügend Werte für die Leistungsberechnung.';
          }
          break;
        }

        case 'CURRENT': {
          if (pInput === null) {
            message = 'Leistungswert erforderlich.';
            break;
          }
          if (!u && !r) {
            message = 'Spannung oder Widerstand angeben.';
            break;
          }
          if (u && u > 0) {
            if (voltageSystem === 'DC') {
              calculatedCurrent = pInput / u;
              apparentPower = pInput;
              message = `<b>📐 Stromberechnung (DC):</b><br/>` +
                `Formel: I = P / U<br/>` +
                `I = ${pInput} W / ${u} V = <b>${calculatedCurrent.toFixed(2)} A</b>`;
            } else if (voltageSystem === 'AC 1-phasig') {
              const effectiveCos = cosPhi ?? 1;
              calculatedCurrent = (powerUnit === 'W' ? pInput : pInput * effectiveCos) / (u * effectiveCos);
              apparentPower = u * calculatedCurrent;
              resultingCosPhi = effectiveCos;
              message = `<b>📐 Stromberechnung (AC 1-phasig):</b><br/>` +
                `Formel: I = P / (U × cos φ)<br/>` +
                `I = ${pInput} ${powerUnit} / (${u} V × ${effectiveCos.toFixed(3)}) = <b>${calculatedCurrent.toFixed(2)} A</b><br/>` +
                `Scheinleistung: S = ${apparentPower.toFixed(2)} VA`;
            } else {
              const effectiveCos = cosPhi ?? 1;
              calculatedCurrent = (powerUnit === 'W' ? pInput : pInput * effectiveCos) /
                (u * (powerUnit === 'W' ? effectiveCos : 1) * Math.sqrt(3));
              apparentPower = Math.sqrt(3) * u * calculatedCurrent;
              resultingCosPhi = effectiveCos;
              message = `<b>📐 Stromberechnung (Drehstrom):</b><br/>` +
                `Formel: I = P / (√3 × U × cos φ)<br/>` +
                `I = ${pInput} ${powerUnit} / (√3 × ${u} V × ${effectiveCos.toFixed(3)}) = <b>${calculatedCurrent.toFixed(2)} A</b><br/>` +
                `Scheinleistung: S = √3 × U × I = ${apparentPower.toFixed(2)} VA`;
            }
            calculatedVoltage = u;
            calculatedPower = pInput;
          } else if (r && r > 0) {
            if (powerUnit === 'VA' && isAcSystem) {
              message = 'Für Berechnungen mit R bitte Leistung in Watt (W) angeben.';
            } else {
              calculatedCurrent = Math.sqrt(pInput / r);
              calculatedVoltage = calculatedCurrent * r;
              calculatedPower = pInput;
              apparentPower = pInput;
              resultingCosPhi = isAcSystem ? cosPhi ?? null : null;
              message = `<b>📐 Stromberechnung mit Widerstand:</b><br/>` +
                `Formel: I = √(P / R)<br/>` +
                `I = √(${pInput} W / ${r} Ω) = √${(pInput/r).toFixed(2)} = <b>${calculatedCurrent.toFixed(2)} A</b><br/>` +
                `Spannung: U = I × R = ${calculatedCurrent.toFixed(2)} A × ${r} Ω = <b>${calculatedVoltage.toFixed(2)} V</b>`;
            }
          }
          break;
        }

        case 'VOLTAGE': {
          if (pInput === null) {
            message = 'Leistungswert erforderlich.';
            break;
          }
          if (!i && !r) {
            message = 'Strom oder Widerstand angeben.';
            break;
          }
          if (i && i > 0) {
            if (voltageSystem === 'DC') {
              calculatedVoltage = pInput / i;
              apparentPower = pInput;
              message = `<b>📐 Spannungsberechnung (DC):</b><br/>` +
                `Formel: U = P / I<br/>` +
                `U = ${pInput} W / ${i} A = <b>${calculatedVoltage.toFixed(2)} V</b>`;
            } else if (voltageSystem === 'AC 1-phasig') {
              const effectiveCos = cosPhi ?? 1;
              calculatedVoltage = (powerUnit === 'W' ? pInput : pInput * effectiveCos) / (i * effectiveCos);
              apparentPower = calculatedVoltage * i;
              resultingCosPhi = effectiveCos;
              message = `<b>📐 Spannungsberechnung (AC 1-phasig):</b><br/>` +
                `Formel: U = P / (I × cos φ)<br/>` +
                `U = ${pInput} ${powerUnit} / (${i} A × ${effectiveCos.toFixed(3)}) = <b>${calculatedVoltage.toFixed(2)} V</b><br/>` +
                `Scheinleistung: S = ${apparentPower.toFixed(2)} VA`;
            } else {
              const effectiveCos = cosPhi ?? 1;
              calculatedVoltage = (powerUnit === 'W' ? pInput : pInput * effectiveCos) /
                (i * (powerUnit === 'W' ? effectiveCos : 1) * Math.sqrt(3));
              apparentPower = Math.sqrt(3) * calculatedVoltage * i;
              resultingCosPhi = effectiveCos;
              message = `<b>📐 Spannungsberechnung (Drehstrom):</b><br/>` +
                `Formel: U = P / (√3 × I × cos φ)<br/>` +
                `U = ${pInput} ${powerUnit} / (√3 × ${i} A × ${effectiveCos.toFixed(3)}) = <b>${calculatedVoltage.toFixed(2)} V</b><br/>` +
                `Scheinleistung: S = √3 × U × I = ${apparentPower.toFixed(2)} VA`;
            }
            calculatedCurrent = i;
            calculatedPower = pInput;
          } else if (r && r > 0) {
            if (powerUnit === 'VA' && isAcSystem) {
              message = 'Für U = √(P·R) bitte Leistung in Watt (W) wählen.';
            } else {
              calculatedVoltage = Math.sqrt(pInput * r);
              calculatedCurrent = calculatedVoltage / r;
              calculatedPower = pInput;
              apparentPower = pInput;
              resultingCosPhi = isAcSystem ? cosPhi ?? null : null;
              message = `<b>📐 Spannungsberechnung mit Widerstand:</b><br/>` +
                `Formel: U = √(P × R)<br/>` +
                `U = √(${pInput} W × ${r} Ω) = √${(pInput*r).toFixed(2)} = <b>${calculatedVoltage.toFixed(2)} V</b><br/>` +
                `Strom: I = U / R = ${calculatedVoltage.toFixed(2)} V / ${r} Ω = <b>${calculatedCurrent.toFixed(2)} A</b>`;
            }
          }
          break;
        }
      }
    } catch (error) {
      console.error('Fehler bei der Berechnung', error);
      message = 'Fehler bei der Berechnung.';
    }

    if (message) {
      setResultHtml(message);
      setCableResultHtml('');
      return;
    }

    // Standard-Ergebnisausgabe (außer EFFICIENCY und REACTIVE_POWER - die haben eigene)
    if (calculationTarget !== 'EFFICIENCY' && calculationTarget !== 'REACTIVE_POWER') {
      const outputLines: string[] = [];

      if (calculationTarget === 'POWER' && calculatedPower !== null) {
        outputLines.push(`<b>Wirkleistung (P):</b> ${formatDecimal(calculatedPower)} W`);
        if (isAcSystem && apparentPower) {
          outputLines.push(`<b>Scheinleistung (S):</b> ${formatDecimal(apparentPower)} VA`);
          if (reactivePower) outputLines.push(`<b>Blindleistung (Q):</b> ${formatDecimal(reactivePower)} var`);
        }
      }
      if (calculationTarget === 'CURRENT' && calculatedCurrent !== null) {
        outputLines.push(`<b>Strom (I):</b> ${formatDecimal(calculatedCurrent)} A`);
      }
      if (calculationTarget === 'VOLTAGE' && calculatedVoltage !== null) {
        outputLines.push(`<b>Spannung (U):</b> ${formatDecimal(calculatedVoltage)} V`);
      }

      if (!outputLines.length) {
        setResultHtml('Berechnung nicht möglich.');
        return;
      }

      const previewCu = calculatedCurrent ? findPreviewCrossSection(calculatedCurrent, 'Kupfer') : null;
      const previewAl = calculatedCurrent ? findPreviewCrossSection(calculatedCurrent, 'Aluminium') : null;

      if (previewCu) {
        outputLines.push(
          `<br><i>Vorl. Mindestquerschnitt (Cu, 30°C, Verlegeart C): ${formatDecimal(previewCu)} mm²</i>`
        );
      }
      if (previewAl) {
        outputLines.push(
          `<i>Vorl. Mindestquerschnitt (Al, 30°C, Verlegeart C): ${formatDecimal(previewAl)} mm²</i>`
        );
      }

      setResultHtml(outputLines.join('<br>'));

      setLastState({
        current: calculatedCurrent,
        voltage: calculatedVoltage,
        power: calculatedPower,
        cosPhi: resultingCosPhi,
        reactivePower,
        apparentPower
      });

      if (calculatedCurrent !== null && calculatedVoltage !== null) {
        setCableResultHtml('Spezifische Kabelparameter für detaillierte Berechnung eingeben.');
      } else {
        setCableResultHtml('');
      }
    }
  };

  const performCableCalculation = () => {
    if (!lastState.current || !lastState.voltage) {
      setCableResultHtml('Vorher muss eine Strom-/Spannungsberechnung erfolgen.');
      return;
    }

    const ambientTemp = numberFromInput(cableInputs.ambientTemp) ?? 30;
    const cableLength = numberFromInput(cableInputs.cableLength);
    const maxVoltageDrop = numberFromInput(cableInputs.maxVoltageDrop) ?? 3;

    if (!cableLength || cableLength <= 0) {
      setCableErrors(prev => ({ ...prev, cableLength: 'Kabellänge erforderlich.' }));
      setCableResultHtml('Eingabefehler bei den Kabelparametern.');
      return;
    }

    const temperatureFactor = getTemperatureCorrectionFactor(ambientTemp);
    const adern = voltageSystem === 'AC 3-phasig' ? 3 : 2;
    const capacityTable = getCapacityTable(cableInputs.conductorMaterial, adern as 2 | 3, cableInputs.installationType);

    if (!capacityTable) {
      setCableResultHtml(`Keine Belastbarkeitsdaten für ${cableInputs.conductorMaterial} / ${cableInputs.installationType}.`);
      return;
    }

    const sorted = [...capacityTable].sort((a, b) => a[0] - b[0]);
    const currentIb = lastState.current;
    const voltageUn = lastState.voltage;
    const cosPhi = lastState.cosPhi ?? 1;

    let requiredCrossSectionLoad = -1;
    for (const [section, baseLoad] of sorted) {
      if (baseLoad * temperatureFactor >= currentIb) {
        requiredCrossSectionLoad = section;
        break;
      }
    }

    if (requiredCrossSectionLoad < 0) {
      setCableResultHtml(`Kein Querschnitt für Strom ${formatDecimal(currentIb)} A gefunden.`);
      return;
    }

    const rho = cableInputs.conductorMaterial === 'Kupfer' ? RHO_KUPFER : RHO_ALUMINIUM;
    const candidates = sorted
      .map(([section]) => section)
      .filter(section => section >= requiredCrossSectionLoad);

    if (!candidates.length) {
      setCableResultHtml(`Kein Standardquerschnitt ≥ ${formatDecimal(requiredCrossSectionLoad)} mm² gefunden.`);
      return;
    }

    const calcDrop = (section: number) => {
      switch (voltageSystem) {
        case 'DC':
          return (2 * cableLength * currentIb * rho) / section;
        case 'AC 1-phasig':
          return (2 * cableLength * currentIb * rho * cosPhi) / section;
        case 'AC 3-phasig':
          return (Math.sqrt(3) * cableLength * currentIb * rho * cosPhi) / section;
        default:
          return 0;
      }
    };

    let finalCrossSection = -1;
    let voltageDropActual = Number.POSITIVE_INFINITY;

    for (const candidate of candidates) {
      const dropPercent = (calcDrop(candidate) / voltageUn) * 100;
      if (dropPercent <= maxVoltageDrop) {
        finalCrossSection = candidate;
        voltageDropActual = dropPercent;
        break;
      }
    }

    if (finalCrossSection < 0) {
      const largest = candidates[candidates.length - 1];
      const dropPercent = (calcDrop(largest) / voltageUn) * 100;
      setCableResultHtml(
        `<b>Achtung:</b> Kein Standardquerschnitt erfüllt ΔU ≤ ${formatDecimal(maxVoltageDrop)} %.<<br>` +
          `Größter geprüfter Querschnitt: ${formatDecimal(largest)} mm² (ΔU = ${formatDecimal(dropPercent)} %).<br>` +
          `Mindestquerschnitt nach Last: ${formatDecimal(requiredCrossSectionLoad)} mm²`
      );
      return;
    }

    const lines = [
      `<b>Empfohlener Mindestquerschnitt:</b> ${formatDecimal(finalCrossSection)} mm²`,
      `(Berechneter Strom: ${formatDecimal(currentIb)} A)`,
      `Mindestquerschnitt (Last): ${formatDecimal(requiredCrossSectionLoad)} mm²`,
      `Spannungsfall mit ${formatDecimal(finalCrossSection)} mm²: ${formatDecimal(voltageDropActual)} % (max. ${formatDecimal(maxVoltageDrop)} %)`
    ];
    setCableResultHtml(lines.join('<br>'));
  };

  const showCableSection = lastState.current !== null && lastState.voltage !== null;

  const actionButtons = useMemo(() => [
    <Tooltip key="info" title="Hintergrundwissen">
      <IconButton onClick={() => setInfoDialogOpen(true)} size="small">
        <InfoOutlinedIcon />
      </IconButton>
    </Tooltip>,
    <Tooltip key="reset" title="Alle Felder zurücksetzen">
      <IconButton onClick={handleReset} size="small">
        <RestartAltIcon />
      </IconButton>
    </Tooltip>,
    <Tooltip key="copy" title="Ergebnis kopieren">
      <span>
        <IconButton onClick={handleCopyResult} size="small" disabled={!resultHtml}>
          <ContentCopyIcon />
        </IconButton>
      </span>
    </Tooltip>
  ], [resultHtml, handleReset, handleCopyResult]);

  const powerUnitSelector = useMemo(() => (
    <RadioGroup
      row
      value={powerUnit}
      onChange={event => setPowerUnit(event.target.value as 'W' | 'VA')}
    >
      <FormControlLabel value="W" control={<Radio color="primary" />} label="W" />
      <FormControlLabel value="VA" control={<Radio color="primary" disabled={voltageSystem === 'DC'} />} label="VA" />
    </RadioGroup>
  ), [powerUnit, voltageSystem]);

  if (!canViewLeistungsberechnung) {
    return (
      <UnitPageShell title="M99 · Leistungsberechnung" showTTS={false}>
        <Alert severity="error">Keine Berechtigung fuer die Leistungsberechnung.</Alert>
      </UnitPageShell>
    );
  }

  return (
    <>
      <UnitPageShell
        title="M99 · Leistungsberechnung"
        actions={actionButtons}
        showTTS={false}
      >
        <Stack spacing={4}>
          {/* Hauptberechnungskarte */}
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
                Parameter
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Berechnungsziel"
                  value={calculationTarget}
                  onChange={event => setCalculationTarget(event.target.value as CalculationTarget)}
                >
                  {calculationTargets.map(target => (
                    <MenuItem key={target.value} value={target.value}>
                      {target.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Spannungssystem"
                  value={voltageSystem}
                  onChange={event => setVoltageSystem(event.target.value as typeof voltageSystems[number])}
                >
                  {voltageSystems.map(system => (
                    <MenuItem key={system} value={system}>
                      {system}
                    </MenuItem>
                  ))}
                </TextField>

                {calculationTarget !== 'POWER' && calculationTarget !== 'REACTIVE_POWER' && (
                  <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary }}>
                      Leistungseinheit:
                    </Typography>
                    {powerUnitSelector}
                  </Box>
                )}

                {/* Wirkungsgrad-spezifische Felder */}
                {calculationTarget === 'EFFICIENCY' && (
                  <>
                    <TextField
                      fullWidth
                      label="Zugeführte Leistung (W)"
                      value={inputs.powerInput}
                      onChange={handleInputChange('powerInput')}
                      error={Boolean(errors.powerInput)}
                      helperText={errors.powerInput}
                      inputMode="decimal"
                    />
                    <TextField
                      fullWidth
                      label="Abgegebene Leistung (W)"
                      value={inputs.powerOutput}
                      onChange={handleInputChange('powerOutput')}
                      error={Boolean(errors.powerOutput)}
                      helperText={errors.powerOutput}
                      inputMode="decimal"
                    />
                  </>
                )}

                {/* Standard-Eingabefelder (außer Wirkungsgrad) */}
                {calculationTarget !== 'EFFICIENCY' && (
                  <>
                    {calculationTarget !== 'VOLTAGE' && (
                      <TextField
                        fullWidth
                        label="Spannung U (V)"
                        value={inputs.voltage}
                        onChange={handleInputChange('voltage')}
                        error={Boolean(errors.voltage)}
                        helperText={errors.voltage}
                        inputMode="decimal"
                      />
                    )}

                    {calculationTarget !== 'CURRENT' && (
                      <TextField
                        fullWidth
                        label="Strom I (A)"
                        value={inputs.current}
                        onChange={handleInputChange('current')}
                        error={Boolean(errors.current)}
                        helperText={errors.current}
                        inputMode="decimal"
                      />
                    )}

                    {calculationTarget !== 'POWER' && calculationTarget !== 'REACTIVE_POWER' && (
                      <TextField
                        fullWidth
                        label={`Leistung P (${powerUnit})`}
                        value={inputs.power}
                        onChange={handleInputChange('power')}
                        error={Boolean(errors.power)}
                        helperText={errors.power}
                        inputMode="decimal"
                      />
                    )}

                    <TextField
                      fullWidth
                      label="Widerstand R (Î©)"
                      value={inputs.resistance}
                      onChange={handleInputChange('resistance')}
                      error={Boolean(errors.resistance)}
                      helperText={errors.resistance}
                      inputMode="decimal"
                    />

                    {isAcSystem && (
                      <TextField
                        fullWidth
                        label="cos Ï†"
                        value={inputs.cosPhi}
                        onChange={handleInputChange('cosPhi')}
                        error={Boolean(errors.cosPhi)}
                        helperText={errors.cosPhi}
                        inputMode="decimal"
                      />
                    )}
                  </>
                )}
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={React.createElement(FunctionsIcon)}
                onClick={performCalculation}
                sx={{ 
                  backgroundColor: tokens.colors.primary, 
                  '&:hover': { backgroundColor: tokens.colors.primaryHover } 
                }}
              >
                Berechnen
              </Button>

              {resultHtml && (
                <>
                  <Divider sx={{ my: 3 }} />
                  
                  <Stack spacing={2}>
                    <Typography
                      variant="body1"
                      component="div"
                      sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.8 }}
                      dangerouslySetInnerHTML={{ __html: resultHtml }}
                    />

                    {/* SVG Visualisierungen */}
                    {isAcSystem && lastState.cosPhi && calculationTarget === 'REACTIVE_POWER' && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <PowerTriangleSVG cosPhi={lastState.cosPhi} darkMode={darkMode} />
                      </Box>
                    )}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>

          {/* Kabelberechnungskarte */}
          {showCableSection && (
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
                  Kabelquerschnittsempfehlung
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                  <TextField
                    select
                    fullWidth
                    label="Verlegeart"
                    value={cableInputs.installationType}
                    onChange={handleCableInputChange('installationType')}
                  >
                    {installationTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Leitermaterial"
                    value={cableInputs.conductorMaterial}
                    onChange={handleCableInputChange('conductorMaterial')}
                  >
                    {conductorMaterials.map(material => (
                      <MenuItem key={material} value={material}>
                        {material}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    label="Umgebungstemperatur (°C)"
                    value={cableInputs.ambientTemp}
                    onChange={handleCableInputChange('ambientTemp')}
                    error={Boolean(cableErrors.ambientTemp)}
                    helperText={cableErrors.ambientTemp}
                    inputMode="decimal"
                  />

                  <TextField
                    fullWidth
                    label="Kabellänge (m)"
                    value={cableInputs.cableLength}
                    onChange={handleCableInputChange('cableLength')}
                    error={Boolean(cableErrors.cableLength)}
                    helperText={cableErrors.cableLength}
                    inputMode="decimal"
                  />

                  <TextField
                    fullWidth
                    label="Max. Spannungsfall (%)"
                    value={cableInputs.maxVoltageDrop}
                    onChange={handleCableInputChange('maxVoltageDrop')}
                    error={Boolean(cableErrors.maxVoltageDrop)}
                    helperText={cableErrors.maxVoltageDrop}
                    inputMode="decimal"
                  />
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={React.createElement(CableIcon)}
                  onClick={performCableCalculation}
                  sx={{ 
                    backgroundColor: tokens.colors.primary, 
                    '&:hover': { backgroundColor: tokens.colors.primaryHover } 
                  }}
                >
                  Kabel berechnen
                </Button>

                {cableResultHtml && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    
                    <Stack spacing={2}>
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.8 }}
                        dangerouslySetInnerHTML={{ __html: cableResultHtml }}
                      />

                      {/* Kabel-SVG wenn Berechnung erfolgreich */}
                      {lastState.current && cableResultHtml.includes('Empfohlener Mindestquerschnitt') && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <CableCrossSectionSVG 
                            crossSection={parseFloat((() => {
                              const re = new RegExp('(\\d+\\.?\\d*)\\s*mm\\u00B2');
                              const m = cableResultHtml.match(re);
                              return m ? m[1] : '0';
                            })())} 
                            current={lastState.current}
                            darkMode={darkMode}
                          />
                        </Box>
                      )}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Stack>
      </UnitPageShell>

      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: tokens.colors.primary, color: '#fff' }}>
          Informationen · Leistungsberechnung
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {infoTopics.map((topic, idx) => (
              <Box key={idx}>
                <Button
                  fullWidth
                  variant={infoTopicIndex === idx ? 'contained' : 'outlined'}
                  onClick={() => setInfoTopicIndex(idx)}
                  sx={{ justifyContent: 'flex-start', mb: 1 }}
                >
                  {topic.title}
                </Button>
                {infoTopicIndex === idx && (
                  <Box sx={{ p: 2, backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ lineHeight: 1.8 }}
                      dangerouslySetInnerHTML={{ __html: topic.html }}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} color="primary">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Page;
